// Every transaction type the app stores. The Add/Edit dialog, the Type filter,
// split parts and import pickers all list these so they never drift.
export const TRANSACTION_TYPES = [
  { value: 'expense', label: 'Expense' },
  { value: 'income', label: 'Income' },
  { value: 'refund', label: 'Refund (money back on an expense)' },
  { value: 'transfer', label: 'Transfer (between my accounts / card payment)' },
  { value: 'adjustment', label: 'Adjustment (balance fix or market value; not income or expense)' },
];

const short = (label) => label.replace(/\s*\(.*\)$/, '');

// Short labels for compact pickers (filters, split parts).
export const TRANSACTION_TYPE_OPTIONS = TRANSACTION_TYPES.map((t) => ({ value: t.value, label: short(t.label) }));

const titled = (v) => String(v).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Options for a select; a stored type not in the list is kept as its own option
// so opening the dialog never silently overwrites it.
export function transactionTypeOptions(storedType, list = TRANSACTION_TYPES) {
  const v = storedType ? String(storedType) : '';
  if (!v || list.some((t) => t.value === v)) return list;
  return [...list, { value: v, label: titled(v) }];
}

// Category types a transaction type may use. A refund is contra-expense
// (#145): it reduces an expense category, so it uses expense categories
// (and any category stored with type refund).
const CATEGORY_TYPES_FOR = {
  income: ['income'],
  expense: ['expense'],
  refund: ['expense', 'refund'],
  transfer: ['transfer'],
  adjustment: [], // any category, or none
};

// Types that may be saved without a category.
const CATEGORY_OPTIONAL_TYPES = ['transfer', 'adjustment'];

// Does this save need a category? New rows (other than transfers and
// adjustments) do. An edit of a row that was saved without one (for example
// an imported row) does not, so a date-only fix can be saved.
export function categoryRequired(data, editing) {
  if (data?.category_id) return false;
  if (CATEGORY_OPTIONAL_TYPES.includes(String(data?.transaction_type || '').toLowerCase())) return false;
  if (editing && !editing.category_id) return false;
  return true;
}

export const categoryTypesFor = (type) => CATEGORY_TYPES_FOR[String(type || '').toLowerCase()] || [];

// Categories offered for a transaction type: matching types plus equity and
// transfer (usable by any row), uncategorised-type rows, and the currently
// selected category so an edit never drops it.
export function categoriesForType(categories, type, selectedId) {
  const allowed = categoryTypesFor(type);
  if (allowed.length === 0) return categories || [];
  return (categories || []).filter((c) => !c.type || allowed.includes(c.type) || c.type === 'equity' || c.type === 'transfer'
    || (selectedId != null && selectedId !== '' && String(c.id) === String(selectedId)));
}

// Categories for a group of rows with several types (import mapping).
export function categoriesForTypes(categories, types) {
  const allowed = new Set((types || []).flatMap(categoryTypesFor));
  return (categories || []).filter((c) => allowed.has(c.type) || c.type === 'equity' || c.type === 'transfer');
}